import { useCallback, useState } from 'react'
import { useDropzone } from 'react-dropzone'
import { cn } from '@/lib/utils'
import { Button, Badge } from '@/components/ui'
import { Upload, X, CheckCircle, AlertCircle, FileJson } from 'lucide-react'

interface UploadedFile {
  file: File
  id: string
  type: 'brcs' | 'brig' | 'amazon' | 'perfios' | 'unknown'
  status: 'pending' | 'uploading' | 'success' | 'error'
  error?: string
}

interface FileUploadProps {
  onFilesSelected: (files: UploadedFile[]) => void
  maxFiles?: number
  className?: string
}

const fileTypeMap: Record<string, UploadedFile['type']> = {
  brcs: 'brcs',
  brig: 'brig',
  amazon: 'amazon',
  perfios: 'perfios',
}

// Common Amazon seller data file patterns
const amazonFilePatterns = [
  'settlement',
  'sales_data',
  'performance',
  'merchant_listing',
  'financialevent',
  'financial_event',
  'form',
  'orders',
  'inventory',
  'payments',
  'transactions',
  'fee_preview',
  'reimbursements',
  'returns',
]

function detectFileType(filename: string): UploadedFile['type'] {
  const lowerName = filename.toLowerCase()
  
  // Check for explicit type keywords first
  for (const [key, type] of Object.entries(fileTypeMap)) {
    if (lowerName.includes(key)) {
      return type
    }
  }
  
  // Check for Amazon-specific file patterns
  for (const pattern of amazonFilePatterns) {
    if (lowerName.includes(pattern)) {
      return 'amazon'
    }
  }
  
  // Check file extension patterns for common data files
  if (lowerName.endsWith('.csv') || lowerName.endsWith('.json')) {
    // If it has customer ID pattern like "Customer-X - filename", assume it's amazon data
    if (lowerName.match(/customer[_-]?\d/i)) {
      return 'amazon'
    }
  }
  
  return 'unknown'
}

const typeLabels: Record<UploadedFile['type'], { label: string; variant: 'default' | 'secondary' | 'success' | 'warning' }> = {
  brcs: { label: 'BRCS', variant: 'success' },
  brig: { label: 'BRIG', variant: 'success' },
  amazon: { label: 'Amazon', variant: 'success' },
  perfios: { label: 'Perfios', variant: 'secondary' },
  unknown: { label: 'Data File', variant: 'default' },
}

export function FileUpload({
  onFilesSelected,
  maxFiles = 10,
  className,
}: FileUploadProps) {
  const [files, setFiles] = useState<UploadedFile[]>([])

  const onDrop = useCallback((acceptedFiles: File[]) => {
    const newFiles: UploadedFile[] = acceptedFiles.map((file) => ({
      file,
      id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: detectFileType(file.name),
      status: 'pending',
    }))

    setFiles((prev) => [...prev, ...newFiles].slice(0, maxFiles))
    onFilesSelected([...files, ...newFiles].slice(0, maxFiles))
  }, [files, maxFiles, onFilesSelected])

  const removeFile = (id: string) => {
    const updated = files.filter((f) => f.id !== id)
    setFiles(updated)
    onFilesSelected(updated)
  }

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/json': ['.json'],
      'text/csv': ['.csv'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
    },
    maxFiles,
  })

  return (
    <div className={cn('space-y-4', className)}>
      {/* Dropzone */}
      <div
        {...getRootProps()}
        className={cn(
          'border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-all',
          isDragActive
            ? 'border-dashboard-teal bg-dashboard-teal/5'
            : 'border-gray-300 hover:border-dashboard-teal hover:bg-gray-50'
        )}
      >
        <input {...getInputProps()} />
        <div className="flex flex-col items-center gap-3">
          <div className={cn(
            'p-4 rounded-full',
            isDragActive ? 'bg-dashboard-teal/10' : 'bg-gray-100'
          )}>
            <Upload className={cn(
              'h-8 w-8',
              isDragActive ? 'text-dashboard-teal' : 'text-gray-400'
            )} />
          </div>
          <div>
            <p className="text-sm font-medium text-gray-700">
              {isDragActive ? 'Drop your files here...' : 'Drag & drop files here'}
            </p>
            <p className="text-xs text-gray-500 mt-1">
              or click to browse • Supports JSON, CSV, XLSX
            </p>
          </div>
        </div>
      </div>

      {/* File list */}
      {files.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-sm font-medium text-gray-700">Selected Files ({files.length})</h4>
          <div className="space-y-2">
            {files.map((file) => {
              const typeInfo = typeLabels[file.type]
              return (
                <div
                  key={file.id}
                  className="flex items-center gap-3 p-3 bg-white rounded-lg border"
                >
                  <div className="p-2 bg-gray-100 rounded">
                    <FileJson className="h-5 w-5 text-gray-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-gray-700 truncate">
                        {file.file.name}
                      </span>
                      <Badge variant={typeInfo.variant as 'default'}>
                        {typeInfo.label}
                      </Badge>
                    </div>
                    <p className="text-xs text-gray-500">
                      {(file.file.size / 1024).toFixed(1)} KB
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    {file.status === 'success' && (
                      <CheckCircle className="h-5 w-5 text-risk-excellent" />
                    )}
                    {file.status === 'error' && (
                      <AlertCircle className="h-5 w-5 text-risk-critical" />
                    )}
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => removeFile(file.id)}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}
